// Attempt, response and evidence lifecycle (handoff §11–§13), independent of
// where records are stored. Every function is safe to call again after a
// timeout: it reads before it writes and never duplicates a record.
//
// An attempt is identified by its release (delivery + activity). The learner
// is the tablet's own, known only to the server side, which derives the
// deterministic sync_key from all three.

export const DRAWING_TYPE = "image/png";

// `currentStep` is the step's position in the activity, because that is what
// the attempt record stores. Step ids remain the app's own vocabulary, and are
// what `responses` is keyed by (handoff §12).
export const FIRST_STEP = 0;

const identity = (release) => ({
  deliveryId: release.deliveryId,
  activityId: release.activityId,
});

// Get-or-create. If the create fails (timeout, conflict, lost response), read
// again and resume what exists.
export const getOrCreateAttempt = async (repo, { release }) => {
  const existing = await repo.findAttempt(identity(release));
  if (existing) return existing;
  try {
    return await repo.createAttempt({
      ...identity(release),
      currentStep: FIRST_STEP,
    });
  } catch (error) {
    const created = await repo.findAttempt(identity(release));
    if (created) return created;
    throw error;
  }
};

// Reads an attempt without creating one: refreshing the waiting screen or
// "Your turn" must leave no trace.
export const findAttempt = (repo, { release }) =>
  repo.findAttempt(identity(release));

// Links drawing evidence once. The repository looks for existing evidence,
// then for an already-uploaded file, before creating either, so a retry after
// a lost response never makes a second of each.
export const saveDrawingEvidence = (repo, attempt, { stepId, blob }) =>
  repo.saveDrawing({ attempt, stepId, blob });

// Hands in one step. Picture work (a drawing, tracing or numeric working) is
// linked as evidence first, so the gateway never records a step whose picture
// it has not kept; an unmarked picture saves no file and no evidence, because a
// blank image is not meaningful work.
//
// The gateway answers with the learner's attempt as it now stands, whether it
// is complete, and the feedback policy permits. Sending the same
// `submissionId` again after a lost answer returns the first result.
export const submitStep = async (
  repo,
  attempt,
  { stepId, submissionId, response, blob = null }
) => {
  if (blob) await saveDrawingEvidence(repo, attempt, { stepId, blob });
  const result = await repo.submitStep(attempt.id, stepId, { submissionId, response });
  return {
    attempt: result.attempt,
    outcome: result.feedback ?? null,
    stepComplete: result.stepComplete === true,
  };
};
